import { twMerge } from 'tailwind-merge';
import { formatCurrency } from '@/utils/currencyUtils';
import { MonetaryAmount } from '@/types';
import { useDateFormatter } from '@acast-tech/decibel';

interface ChartTooltipWrapperProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Wrapper component for chart tooltips providing consistent styling
 */
export const ChartTooltipWrapper = ({ children, className }: ChartTooltipWrapperProps) => {
  return (
    <div
      role="tooltip"
      className={twMerge(
        'p-2 rounded-md flex items-center border-[1px] border-primary/25 bg-[#505050] text-white',
        className
      )}
    >
      {children}
    </div>
  );
};

interface BaseTooltipPayload {
  name: string;
  color: string;
  total: MonetaryAmount;
}

export interface CurrencyTooltipPayload extends BaseTooltipPayload {
  type: 'currency';
}

interface EarningsTooltipPayload extends BaseTooltipPayload {
  type: 'date/earnings';
  calculation_date: string;
  impressions: number;
}

type TooltipPayload = CurrencyTooltipPayload | EarningsTooltipPayload;

interface ChartTooltipContentProps {
  payload: TooltipPayload | null;
  type: TooltipPayload['type'];
}

/**
 * Content component for chart tooltips displaying value and currency information
 */
export const ChartTooltipContent = ({ payload, type }: ChartTooltipContentProps) => {
  const formatter = useDateFormatter({ month: 'short', day: 'numeric' });
  if (!payload) return null;

  const { name, color, total } = payload;

  return (
    <div className="flex-col">
      {type === 'currency' && (
        <div className="inline-flex items-center">
          <div className={`w-3 h-3 rounded-full mr-2 bg-${color}`} />
          <p className="text-sm font-medium capitalize mr-6">{name}</p>
          <p className="text-sm ">{formatCurrency(total?.amount || 0, total?.currency)}</p>
        </div>
      )}
      {type === 'date/earnings' && (payload as EarningsTooltipPayload).calculation_date && (
        <>
          <p className="text-sm font-medium capitalize mr-6">
            {formatter.format(new Date((payload as EarningsTooltipPayload).calculation_date))}
          </p>
          <div className="inline-flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 bg-${color}`} />
            <p className="text-sm">
              Estimated earnings:{' '}
              <span className="font-bold">
                {formatCurrency(total?.amount || 0, total?.currency, true)}
              </span>
            </p>
          </div>
        </>
      )}
    </div>
  );
};
